import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { savePreferences, loadPreferences } from '@/shared/api'
import { useAuthStore } from '@/shared/session'

interface SettingsStore {
  /** Enables the `anim:` Tailwind variant (wizard, dialogs, sheet). Off by default. Persisted. */
  animations: boolean
  setAnimations: (animations: boolean) => void
  /**
   * Whether the "want animations?" prompt has been answered — once per device
   * (persisted locally), or once per account once signed in (mirrored below,
   * same as `animations`). Never un-set: a missing or false cloud value never
   * overrides a local yes, it only ever gets pushed the other way.
   */
  animationsPromptSeen: boolean
  /** Answers the prompt; `true` also turns animations on. Won't ask again either way. */
  answerAnimationsPrompt: (enable: boolean) => void
  /**
   * True once `loadSettingsFromCloud` has settled this session (immediately, if
   * never signed in). Not persisted — a fresh session must wait for its own
   * check, not reuse a flag left over from a previous one. Lets something like
   * `AnimationsPrompt` wait for the account's own answer without calling
   * `loadSettingsFromCloud` a second time itself (App.tsx already does, on sign-in).
   */
  cloudSettingsChecked: boolean
  /** Load persisted app settings from the cloud. Called on sign-in. */
  loadSettingsFromCloud: () => Promise<void>
}

// The debounce and the create-or-patch dance both live in `savePreferences`,
// which `rollStore` shares: the two write disjoint halves of one record.
function scheduleSettingsSync() {
  savePreferences(useAuthStore.getState().user?.id, 'app_settings', () => ({
    animations: useSettingsStore.getState().animations,
    animationsPromptSeen: useSettingsStore.getState().animationsPromptSeen,
  }))
}

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set) => ({
      animations: false,
      animationsPromptSeen: false,
      cloudSettingsChecked: false,
      setAnimations: (animations) => { set({ animations }); scheduleSettingsSync() },
      answerAnimationsPrompt: (enable) => {
        set({ animationsPromptSeen: true, ...(enable ? { animations: true } : {}) })
        scheduleSettingsSync()
      },
      loadSettingsFromCloud: async () => {
        const p = await loadPreferences<Partial<{ animations: boolean; animationsPromptSeen: boolean }>>(
          useAuthStore.getState().user?.id,
          'app_settings',
        )
        if (p?.animations !== undefined) set({ animations: p.animations })
        if (p?.animationsPromptSeen) set({ animationsPromptSeen: true })
        else if (useSettingsStore.getState().animationsPromptSeen) scheduleSettingsSync()
        set({ cloudSettingsChecked: true })
      },
    }),
    {
      name: 'dnd-settings',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ animations: state.animations, animationsPromptSeen: state.animationsPromptSeen }),
    },
  ),
)

/** Reflect the animation preference as the root class the `anim:` variant gates on. */
function applyAnimationClass(on: boolean) {
  if (typeof document === 'undefined') return
  document.documentElement.classList.toggle('animate-on', on)
}

// Apply on load (before first paint, since this module is imported at startup)
// and keep the class in sync with the persisted preference thereafter.
if (typeof document !== 'undefined') {
  applyAnimationClass(useSettingsStore.getState().animations)
  useSettingsStore.subscribe((s) => applyAnimationClass(s.animations))
}
